import { brandPurple } from '@src/cnst/colors.cnst'
import { Button } from '@src/components/button/Button.component'
import { InputNc } from '@src/components/input-nc/InputNc.component'
import { SignupButton } from '@src/components/signup-button/SignupButton.component'
import { useAppDispatch } from '@src/hooks/useAppDispatch'
import { useAppSelector } from '@src/hooks/useAppSelector'
import { useTranslation } from '@src/hooks/useTranslation'
import { mixpanelService } from '@src/srv/mixpanel.service'
import { setDiscountCode, setDiscountPercentage } from '@src/store/discountCode/discountCode.slice'
import { GeolocationCountry } from '@src/store/geolocation/geolocation.slice'
import { DatoCmsFileField } from 'dato-cms-assets'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { NcImage } from '../nc-image/NcImage.component'
import styles from './PopupSms.module.scss'

export interface PopupSmsProps {
  name: string
  isOpen: boolean
  _modelApiKey: 'sms_popup'
  formLabel: string
  formTitle: string
  formText: string
  phoneNumberInputLabel: string
  buttonText: string
  disclaimer: string
  successTitle: string
  successText: string
  promoCode?: string
  discountPercentage: string
  image: DatoCmsFileField
  onDismiss: () => void
}

enum PopupSmsSteps {
  Form = 'Form',
  Success = 'Success',
  Signup = 'Signup',
}

export function PopupSms({
  name,
  isOpen,
  formLabel,
  formTitle,
  formText,
  phoneNumberInputLabel,
  buttonText,
  disclaimer,
  successText,
  successTitle,
  promoCode,
  discountPercentage,
  image,
  onDismiss,
}: PopupSmsProps): ReactNode {
  const dispatch = useAppDispatch()
  const button = useRef<any>()
  const translate = useTranslation()
  const hasOpened = useRef(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [step, setStep] = useState(PopupSmsSteps.Form)
  const [error, setError] = useState(false)

  // Used to decide phone number requirements
  // and signup list id for attentive in the backend
  const country = useAppSelector(state => state.geolocation.country)

  const onOpen = useCallback(function onOpen() {
    // Focusing an element inside the modal avoids the problem of the modal
    // appearing visually but going unnoticed by screen readers, whose focus
    // remains on the main page content behind, often with no clear path forward
    // for the user to rectify the situation.
    button.current.focus()

    hasOpened.current = true
    mixpanelService.track('SmsPopupImpression', {
      name,
    })
  }, [])

  const onError = useCallback(function onError() {
    setError(true)
    mixpanelService.track('SmsPopupError', {
      name,
    })
  }, [])

  const onSuccess = useCallback(
    function onSuccess() {
      if (promoCode) {
        setStep(PopupSmsSteps.Signup)
      } else {
        setStep(PopupSmsSteps.Success)
      }
      mixpanelService.track('SmsPopupSuccess', {
        name,
      })
    },
    [phoneNumber],
  )

  const onSubmit = useCallback(
    function onSubmit() {
      // If the user haven't entered a number yet we return early
      if (phoneNumber === '') {
        return
      }

      let numberToSend = phoneNumber

      // additional checks to make sure the typed number is correct in each country
      if (country === GeolocationCountry.US) {
        const correctUsNumber =
          (phoneNumber.startsWith('+1') && phoneNumber.length === 12) || phoneNumber.length === 10

        if (!correctUsNumber) {
          setError(true)
          return
        }
      } else if (country === GeolocationCountry.GB) {
        const correctCountryCode = phoneNumber.startsWith('+44')
        const withoutCountryCode = phoneNumber.startsWith('07')
        const incorrectCountryCodeFormatting = phoneNumber.startsWith('0044')
        const correctLength = phoneNumber.length >= 10

        if (correctLength && withoutCountryCode) {
          numberToSend = `+44${phoneNumber.slice(1)}`
        } else if (correctLength && incorrectCountryCodeFormatting) {
          numberToSend = `+44${phoneNumber.slice(4)}`
        } else if (correctLength && correctCountryCode) {
          // do nothing with the number as it is correct
        } else {
          setError(true)
          return
        }
      }

      fetch('/.netlify/functions/attentive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: numberToSend,
          country,
        }),
      })
        .then(res => {
          const status = res.status
          if (status === 200) {
            onSuccess()
          } else {
            onError()
          }
        })
        .catch(() => onError())
    },
    [phoneNumber],
  )

  const onClose = useCallback(function onClose() {
    mixpanelService.track('SmsPopupClose', {
      name,
    })
  }, [])

  useEffect(() => {
    if (isOpen) {
      onOpen()
    } else if (hasOpened.current) {
      onClose()
    }
  }, [isOpen])

  const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
    'aria-label': translate('popup-close-button'),
    className: styles.PopupSms__closeButton,
    onClick: onDismiss,
  }

  if (discountPercentage.length < 1) {
    discountPercentage = ''
  }

  const saveDiscountCode = useCallback(() => {
    if (promoCode) {
      dispatch(setDiscountCode(promoCode, 'SmsPopup'))
    }
    dispatch(setDiscountPercentage(discountPercentage))
    mixpanelService.track('PopupSmsSaveDiscountCode', {
      name,
    })
    onDismiss()
  }, [dispatch])

  useEffect(() => {
    setError(false)
  }, [phoneNumber])

  return (
    <div className={styles.PopupSms}>
      <button {...buttonProps} ref={button}>
        <svg height={28} width={28} xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 42 42">
          <circle
            cx={17.5}
            cy={17.5}
            r={20}
            fill={brandPurple}
            className={styles.PopupSms__closeIconBackground}
          />
          <polygon
            className={styles.PopupSms__closeIconForeground}
            points="27.661 9.366 26.325 8 18 16.63 9.675 8 8.339 9.366 16.661 18 8.339 26.634 9.675 28 18 19.37 26.325 28 27.661 26.634 19.339 18 27.661 9.366"
          />
        </svg>
      </button>
      <div className={styles.PopupSms__content}>
        {step === PopupSmsSteps.Form && (
          <div className={styles.PopupSms__formStep}>
            <p className={styles.PopupSms__formLabel}> {formLabel} </p>
            <div
              id="popup-title"
              className={styles.PopupSms__formTitle}
              dangerouslySetInnerHTML={{ __html: formTitle }}
            />
            <div
              id="popup-description"
              className={styles.PopupSms__formText}
              dangerouslySetInnerHTML={{ __html: formText }}
            />
            <div>
              <InputNc
                setValue={setPhoneNumber}
                id="phone-input"
                type="tel"
                label={phoneNumberInputLabel}
                keyPressEvent={onSubmit}
                errorMessage="Your phone number has an invalid format"
                mixPanelServiceTrackName={name}
                mixPanelTrackProp="SmsPopupFocus"
                showErrorFromParent={error}
              />
            </div>
            <div className={styles.PopupSms__signupButton}>
              <Button theme="brandPurple" onClick={onSubmit}>
                {buttonText}
              </Button>
            </div>
            <p className={styles.PopupSms__disclaimer}>{disclaimer}</p>
          </div>
        )}

        {step === PopupSmsSteps.Success && (
          <div className={styles.PopupSms__successStep}>
            <h1 className={styles.PopupSms__successTitle}>{successTitle}</h1>
            <div
              className={styles.PopupSms__successText}
              dangerouslySetInnerHTML={{ __html: successText }}
            />
          </div>
        )}

        {step === PopupSmsSteps.Signup && (
          <div className={styles.PopupSms__signupStep}>
            <h1 className={styles.PopupSms__signupTitle}>{successTitle}</h1>
            <div
              className={styles.PopupSms__signupText}
              dangerouslySetInnerHTML={{ __html: successText }}
            />
            <p className={styles.PopupSms__signupCode}>
              {' '}
              Your discount code: <b>{promoCode}</b>{' '}
            </p>
            <div
              className={styles.PopupSms__buttons}
              aria-label={`Sign up with discount code ${promoCode}`}
            >
              <SignupButton theme="brandPurple" code={promoCode}>
                Sign up with discount
              </SignupButton>
              <Button theme="ghost" onClick={saveDiscountCode}>
                Save discount for later
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className={styles.PopupSms__image}>
        <NcImage {...image} objectFit="cover" />
      </div>
    </div>
  )
}
